import { useState } from 'react';
import { ShieldCheck, ArrowLeft } from 'lucide-react';
import { useVerify2FA } from '@/hooks/use-two-factor';
import { useAuthStore, type AuthTenant } from '@/stores/auth-store';
import { api } from '@/lib/api-client';

interface TwoFactorVerifyProps {
  pendingToken: string;
  isAdmin: boolean;
  onBack: () => void;
}

interface TenantResponse {
  data: AuthTenant | null;
}

export function TwoFactorVerify({ pendingToken, isAdmin, onBack }: TwoFactorVerifyProps) {
  const verify = useVerify2FA();
  const { setAuth } = useAuthStore();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!code.trim()) {
      setError(useBackupCode ? 'Backup code is required' : 'Verification code is required');
      return;
    }

    try {
      const res = await verify.mutateAsync({ pendingToken, code: code.trim() });
      const user = res.data.user;

      // Fetch tenant if user has one
      let tenant: AuthTenant | null = null;
      if (user.tenantId && !isAdmin) {
        try {
          const tenantRes = await api.get<TenantResponse>('/tenants/me');
          tenant = tenantRes.data;
        } catch {
          // Tenant fetch may fail
        }
      }

      setAuth(user, tenant);
      window.location.hash = isAdmin ? '#/admin' : '#/';
    } catch (err: any) {
      setError(err?.error?.message || 'Verification failed');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30">
      <div className="w-full max-w-sm space-y-6 rounded-xl border border-border bg-white p-8 shadow-sm">
        <div className="text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <ShieldCheck className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Two-factor Authentication</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {useBackupCode ? 'Enter one of your backup codes' : 'Enter the 6-digit code from your authenticator app'}
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {error && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              {useBackupCode ? 'Backup code' : 'Verification code'}
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setError('');
              }}
              autoFocus
              autoComplete="one-time-code"
              inputMode={useBackupCode ? 'text' : 'numeric'}
              maxLength={useBackupCode ? 9 : 6}
              className="h-10 w-full rounded-lg border border-input px-3 text-center font-mono text-lg tracking-widest outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              placeholder={useBackupCode ? 'XXXX-XXXX' : '000000'}
            />
          </div>

          <button
            type="submit"
            disabled={verify.isPending}
            className="h-10 w-full rounded-lg bg-primary text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {verify.isPending ? 'Verifying...' : 'Verify'}
          </button>
        </form>

        <div className="space-y-2 text-center">
          <button
            type="button"
            onClick={() => {
              setUseBackupCode(!useBackupCode);
              setCode('');
              setError('');
            }}
            className="text-sm text-primary hover:underline"
          >
            {useBackupCode ? 'Use authenticator app instead' : 'Use a backup code instead'}
          </button>

          <button
            type="button"
            onClick={onBack}
            className="flex w-full items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
}
